import * as React from "react";
import { cn } from "../cn";
import { ChartCard } from "./chart-card";
import { paletteColor } from "./chart-theme";

export interface BarListItem {
  label: string;
  value: number;
  /** Optional secondary text shown to the right of the value. */
  hint?: string;
  color?: string;
}

export interface BarListProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  items: BarListItem[];
  /** Format the numeric value for display (default: vi-VN integer). */
  valueFormatter?: (value: number) => string;
  emptyText?: string;
  className?: string;
}

/**
 * Horizontal proportion bars (label + value + filled track). A lightweight,
 * dependency-free alternative to a bar chart for ranked breakdowns like
 * "revenue by pack" or "invites by status".
 */
export function BarList({
  title,
  subtitle,
  action,
  items,
  valueFormatter,
  emptyText = "Chưa có dữ liệu",
  className,
}: BarListProps) {
  const max = items.reduce((m, it) => Math.max(m, it.value), 0);
  const fmt = valueFormatter ?? ((v: number) => v.toLocaleString("vi-VN"));
  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      action={action}
      className={className}
    >
      {items.length === 0 ? (
        <p className="py-6 text-center text-xs text-ash">{emptyText}</p>
      ) : (
        <ul className="space-y-2.5">
          {items.map((it, i) => {
            const pct = max > 0 ? Math.max((it.value / max) * 100, 2) : 0;
            const color = it.color ?? paletteColor(i);
            return (
              <li key={it.label}>
                <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                  <span className="truncate font-medium text-ink/80">
                    {it.label}
                  </span>
                  <span className="shrink-0 font-semibold text-ink">
                    {fmt(it.value)}
                    {it.hint ? (
                      <span className="ml-1 font-normal text-ash">
                        {it.hint}
                      </span>
                    ) : null}
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-mist">
                  <div
                    className={cn("h-full rounded-full")}
                    style={{ width: `${pct}%`, backgroundColor: color }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </ChartCard>
  );
}
